// GYM-596.2 — member-portal ThemeProvider.
//
// Applies the tenant's public theme to the member portal only:
//   • CSS vars for --color-primary-* and --color-accent-* ramps
//   • Font loading from Google Fonts CDN + CSS var injection
//   • Favicon swap
//   • Exposes logo URLs via ThemeContext
//
// Intentionally NOT added to AppProviders — the staff app stays GymViz-branded.

import { useEffect } from "react";

import { usePublicTheme } from "@/lib/theme/useTheme";
import { FONT_MAP, FONT_DISPLAY_NAMES } from "@/lib/theme/fontConstants";
import { ThemeContext, type ThemeContextValue } from "@/lib/theme/themeContext";

// ---------------------------------------------------------------------------
// Tonal ramp generator
// ---------------------------------------------------------------------------

/** Parse a 6-digit hex string to [r, g, b] (0-255). Returns null if invalid. */
function parseHex(hex: string): [number, number, number] | null {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}


/** Convert r,g,b (0-255) to space-separated channel string required by tokens.css.
 *  e.g. toChannels(36,191,142) → "36 191 142"
 *  This format lets Tailwind / inline styles use: rgb(var(--color-primary-500) / alpha)
 */
function toChannels(r: number, g: number, b: number): string {
  return [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v)))).join(" ");
}

function blend(
  base: [number, number, number],
  target: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    base[0] + (target[0] - base[0]) * t,
    base[1] + (target[1] - base[1]) * t,
    base[2] + (target[2] - base[2]) * t,
];
}

/**
 * Generate a 9-stop ramp (50, 100, 200, 300, 400, 500, 600, 700, 800, 900)
 * from a seed hex anchored at the 500 stop.
 *
 * Lighter shades (50–400) blend the seed toward white.
 * Darker shades (600–900) blend toward a darkened version of the seed.
 */
function buildRamp(seedHex: string): Record<string, string> | null {
  const seed = parseHex(seedHex);
  if (!seed) return null;

  const white: [number, number, number] = [255, 255, 255];
  // Dark target: seed at 35% brightness
  const dark: [number, number, number] = [
    seed[0] * 0.25,
    seed[1] * 0.25,
    seed[2] * 0.25,
  ];

  // Lighter stops: 50→400 blend from seed toward white
  // t=0 is seed, t=1 is white
  const lighterStops: [string, number][] = [
    ["50", 0.88],
    ["100", 0.74],
    ["200", 0.56],
    ["300", 0.38],
    ["400", 0.18],
  ];
  // Darker stops: 600→900 blend from seed toward dark
  const darkerStops: [string, number][] = [
    ["600", 0.18],
    ["700", 0.38],
    ["800", 0.58],
    ["900", 0.76],
  ];

  const ramp: Record<string, string> = {};

  for (const [stop, t] of lighterStops) {
    const [r, g, b] = blend(seed, white, t);
    ramp[stop] = toChannels(r, g, b);
  }

  // 500 stop: store as space-separated channels so rgb(var(--color-primary-500) / alpha) works
  ramp["500"] = toChannels(seed[0], seed[1], seed[2]);

  for (const [stop, t] of darkerStops) {
    const [r, g, b] = blend(seed, dark, t);
    ramp[stop] = toChannels(r, g, b);
  }

  return ramp;
}

// ---------------------------------------------------------------------------
// Side-effect helpers
// ---------------------------------------------------------------------------

function applyColorRamp(prefix: string, ramp: Record<string, string>): void {
  const root = document.documentElement;
  for (const [stop, hex] of Object.entries(ramp)) {
    root.style.setProperty(`--color-${prefix}-${stop}`, hex);
  }
}

function applyFont(fontKey: string): void {
  const family = FONT_MAP[fontKey];
  if (!family) return;

  // Inject <link> if not already present
  const linkId = `gymviz-tenant-font-${fontKey}`;
  if (!document.getElementById(linkId)) {
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }

  // Set the CSS var and html font-family
  const displayName = FONT_DISPLAY_NAMES[fontKey] ?? family.replace("+", " ");
  document.documentElement.style.setProperty("--font-tenant", `"${displayName}", sans-serif`);
  document.documentElement.style.fontFamily = `"${displayName}", var(--font-base, sans-serif)`;
}

function applyFavicon(url: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

function clearOverrides(): void {
  const root = document.documentElement;
  const stops = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"];
  for (const stop of stops) {
    root.style.removeProperty(`--color-primary-${stop}`);
    root.style.removeProperty(`--color-accent-${stop}`);
  }
  root.style.removeProperty("--font-tenant");
  root.style.removeProperty("font-family");
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { data, isLoading } = usePublicTheme();

  useEffect(() => {
    if (!data) return;

    // Primary ramp
    if (data.brand_primary_color) {
      const ramp = buildRamp(data.brand_primary_color);
      if (ramp) applyColorRamp("primary", ramp);
    }

    // Accent ramp — only 300/500/700 in tokens.css; apply same ramp and let
    // the CSS pick what it needs.
    if (data.brand_accent_color) {
      const ramp = buildRamp(data.brand_accent_color);
      if (ramp) {
        const root = document.documentElement;
        root.style.setProperty("--color-accent-300", ramp["300"]);
        root.style.setProperty("--color-accent-500", ramp["500"]);
        root.style.setProperty("--color-accent-700", ramp["700"]);
      }
    }

    // Font
    if (data.brand_font_family) {
      applyFont(data.brand_font_family);
    }

    // Favicon
    if (data.favicon_url) {
      applyFavicon(data.favicon_url);
    }

    return () => {
      clearOverrides();
    };
  }, [data]);

  const value: ThemeContextValue = {
    logoUrl: data?.logo_url ?? null,
    logoDarkUrl: data?.logo_dark_url ?? null,
    legalName: data?.legal_name ?? null,
    isLoading,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

